import { FC, useEffect, useState } from 'react';
import {
  Drawer,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  FormControl,
  TextField,
  Collapse,
  CircularProgress,
} from '@mui/material';
import { XIcon, CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { commonStyles } from '../../../utils/styles/inputs';
import MessagePopup from '../../common/MessagePopup';
import { useAlert } from '../../../hooks/useAlert';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useDashboard } from '../../../hooks/useDashboard';
import {
  getEvalConfigPromptsApi,
  updateEvalConfigPromptsApi,
} from '../../../utils/apis';
import CofirmationModal from '../../modals/CofirmationModal';

interface EvalConfigDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface PromptField {
  key: string;
  label: string;
  placeholder: string;
}

const fields: PromptField[] = [
  {
    key: 'BUILD_PAYLOAD_FOR_GPT',
    label: 'Build Payload for GPT',
    placeholder: 'Enter the JSON structure for building the GPT payload...',
  },
  {
    key: 'GEMINI_DIARIZATION_PROMPT',
    label: 'Gemini Diarization Prompt',
    placeholder: 'Enter the prompt for Gemini diarization transcription...',
  },
  {
    key: 'GPT_ANALYSIS_PROMPT',
    label: 'GPT Analysis Prompt',
    placeholder: 'Enter the prompt for GPT KPI analysis...',
  },
];

const EvalConfigDrawer: FC<EvalConfigDrawerProps> = ({ open, onClose }) => {
  const { selectedAssistant } = useDashboard();
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();
  const [localValues, setLocalValues] = useState<any>({});
  const [originalValues, setOriginalValues] = useState<any>({});
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  // State to track collapsed fields - by default all collapsed
  const [collapsedFields, setCollapsedFields] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    fields.forEach(field => {
      initial[field.key] = true; // All collapsed by default
    });
    return initial;
  });

  // Fetch prompts when drawer opens
  const { data: promptsData, isLoading: isLoadingPrompts } = useQuery({
    queryKey: ['eval-config-prompts', selectedAssistant?._id],
    queryFn: () => getEvalConfigPromptsApi(selectedAssistant?._id || ''),
    enabled: open && !!selectedAssistant?._id,
  });

  useEffect(() => {
    if (
      promptsData &&
      typeof promptsData === 'object' &&
      'data' in promptsData
    ) {
      const responseData = promptsData as any;
      if (responseData?.data) {
        // Extract the three prompt fields from the response
        const prompts = {
          BUILD_PAYLOAD_FOR_GPT: responseData.data.BUILD_PAYLOAD_FOR_GPT || '',
          GEMINI_DIARIZATION_PROMPT:
            responseData.data.GEMINI_DIARIZATION_PROMPT || '',
          GPT_ANALYSIS_PROMPT: responseData.data.GPT_ANALYSIS_PROMPT || '',
        };
        setLocalValues(prompts);
        setOriginalValues(prompts);
      }
    }
  }, [promptsData]);

  const handleInputChange = (key: string, value: string) => {
    setLocalValues((prev: any) => ({
      ...prev,
      [String(key)]: value,
    }));
  };

  const toggleFieldCollapse = (fieldKey: string) => {
    setCollapsedFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return fields.some(field => {
      const originalValue = originalValues[field.key] || '';
      const currentValue = localValues[field.key] || '';
      return originalValue !== currentValue;
    });
  };

  const resetStates = () => {
    setLocalValues(originalValues);
    // Reset collapsed fields to all collapsed
    const resetCollapsed: Record<string, boolean> = {};
    fields.forEach(field => {
      resetCollapsed[field.key] = true;
    });
    setCollapsedFields(resetCollapsed);
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setIsDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    resetStates();
    setIsDiscardModalOpen(false);
    onClose();
  };

  const handleKeepEditing = () => {
    setIsDiscardModalOpen(false);
  };

  const { mutateAsync: updatePromptsAsync, isPending: updatePromptsPending } =
    useMutation({
      mutationKey: ['update-eval-config-prompts'],
      mutationFn: updateEvalConfigPromptsApi,
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ['eval-config-prompts'],
        });
        onClose();
        showAlert('Evaluation prompts updated successfully', 'success');
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message ||
            'Failed to update evaluation prompts',
          'error'
        );
      },
    });

  const handleSave = async () => {
    await updatePromptsAsync({
      agentId: selectedAssistant?._id || '',
      BUILD_PAYLOAD_FOR_GPT: localValues.BUILD_PAYLOAD_FOR_GPT || '',
      GEMINI_DIARIZATION_PROMPT: localValues.GEMINI_DIARIZATION_PROMPT || '',
      GPT_ANALYSIS_PROMPT: localValues.GPT_ANALYSIS_PROMPT || '',
    });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: '800px',
              p: 0,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Sticky Header Section */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#FFFFFF',
            px: 2,
            pt: 2,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            {/* Close Button and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                }}
              >
                <XIcon size={18} />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: 'custom.textBlue',
                  letterSpacing: '-0.2px',
                }}
              >
                Evaluation Prompts Configuration
              </Typography>
            </Box>

            {/* Save Button */}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={updatePromptsPending || !hasUnsavedChanges()}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              {updatePromptsPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>

          <Divider
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.08)',
              mb: 2,
              mx: -2,
            }}
          />
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            px: 2,
            pb: 2,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {isLoadingPrompts ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {fields.map((field, index) => {
                const isCollapsed = collapsedFields[field.key];
                const hasValue =
                  localValues[field.key] &&
                  localValues[field.key].trim() !== '';

                return (
                  <FormControl fullWidth key={index}>
                    {/* Field Header - clickable to expand/collapse */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '8px 4px',
                        borderRadius: '4px',
                      }}
                      onClick={() => toggleFieldCollapse(field.key)}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          padding: '2px',
                          marginRight: '8px',
                          color: '#666',
                        }}
                      >
                        {isCollapsed ? (
                          <CaretRightIcon size={16} />
                        ) : (
                          <CaretDownIcon size={16} />
                        )}
                      </IconButton>

                      <Typography
                        sx={{
                          ...commonStyles.formLabel,
                          margin: 0,
                          flexGrow: 1,
                          fontWeight: hasValue ? 600 : 400,
                          color: hasValue ? '#2B2F32' : 'inherit',
                        }}
                      >
                        {field.label}
                      </Typography>
                    </Box>

                    {/* Preview Box - shown when collapsed and has value */}
                    {hasValue && isCollapsed && (
                      <Box
                        sx={{
                          marginTop: '4px',
                          marginLeft: '32px',
                          padding: '8px 12px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px',
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleFieldCollapse(field.key)}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#666',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {localValues[field.key]}
                        </Typography>
                      </Box>
                    )}

                    {/* Expanded Text Area */}
                    <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          marginTop: '4px',
                          marginLeft: '32px',
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        <TextField
                          id={`field-${String(field.key)}`}
                          name={String(field.key)}
                          placeholder={field.placeholder}
                          fullWidth
                          variant="outlined"
                          value={localValues[String(field.key)] || ''}
                          onChange={e =>
                            handleInputChange(String(field.key), e.target.value)
                          }
                          multiline
                          minRows={6}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '14px',
                              borderRadius: '6px',
                              backgroundColor: '#FFFFFF',
                              transition: 'all 0.2s ease-in-out',
                              fontFamily: 'monospace',
                              '& fieldset': {
                                borderColor: '#E0E0E0',
                                borderWidth: '1px',
                                transition: 'border-color 0.2s ease-in-out',
                              },
                              '&:hover fieldset': {
                                borderColor: '#DCE0E7',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '13px',
                              color: '#302B3F',
                              backgroundColor: '#FFFFFF',
                              fontFamily: 'monospace',
                              '&::placeholder': {
                                color: '#6B6F80',
                              },
                            },
                          }}
                        />
                      </Box>
                    </Collapse>
                  </FormControl>
                );
              })}
            </Box>
          )}
        </Box>
      </Drawer>

      <CofirmationModal
        open={isDiscardModalOpen}
        onClose={handleKeepEditing}
        onConfirm={handleDiscardChanges}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmButtonText="Discard"
        cancelButtonText="Keep Editing"
        isPending={false}
      />

      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default EvalConfigDrawer;
